import { useQuery } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import React, { Fragment, ReactNode } from 'react';

import { Spinner } from '@zapier/design-system';
import { Currency, Price, useCurrencies } from '@zapier/currency';

import { useCurrentAccountId } from 'app/billing/hooks';

export const teamsPricingAccountQuery = gql`
  query teamsPricingAccountQuery($accountId: Int!) {
    account(id: $accountId) {
      currency
      id
      plan: planV2 {
        id
        interval
      }
    }
  }
`;

export const teamsPricingQuery = gql`
  query teamsPricingQuery($accountId: Int) {
    currentUser: currentUserV2 {
      id
    }

    planStacks: pricingPageV3Plans(accountId: $accountId) {
      plans {
        amount
        id
        interval
        name
      }
      planType
    }
  }
`;

export const _useData = () => {
  /**
   * TL;DR:
   *
   * Determine if the user is logged in. If so, grab their current plan
   * interval and their currency. If not, default to yearly billing & USD.
   */
  const { accountId } = useCurrentAccountId();
  const { currencies, isLoading: isLoadingCurrencies } = useCurrencies({
    useQuery,
  });

  const { data, loading: isLoading, error } = useQuery(teamsPricingQuery, {
    variables: {
      accountId,
    },
  });

  const {
    data: accountData,
    loading: isAccountDataLoading,
    error: accountError,
  } = useQuery(teamsPricingAccountQuery, {
    skip: !data?.currentUser || !accountId,
    variables: {
      accountId,
    },
  });

  const currentPlanInterval = accountData?.account?.plan?.interval;

  const teamPlanStack = data?.planStacks?.find(
    planStack => planStack.planType === 'Team'
  );

  const lowestTeamPlan = teamPlanStack?.plans?.find(
    plan => plan.interval === (currentPlanInterval ?? 'year') // will default to year when user is logged out
  );

  const isAnnual = Boolean(lowestTeamPlan?.interval === 'year');

  return {
    currency: currencies?.find(
      (c: Currency) => c.name === accountData?.account?.currency
    ),
    error:
      error || accountError
        ? 'There was an error fetching pricing information. Please try again.'
        : null,
    isAnnual,
    isLoading: isLoading || isAccountDataLoading || isLoadingCurrencies,
    monthlyAmount: isAnnual
      ? lowestTeamPlan?.amount / 12
      : lowestTeamPlan?.amount,
  };
};

type TeamsPricingChildProps = {
  currency: Currency;
  isAnnual: boolean;
  price: number;
};

type TeamsPricingProps = {
  children: ({
    currency,
    isAnnual,
    price,
  }: TeamsPricingChildProps) => ReactNode;
  /** For dependency injection testing only */
  useData?: () => ReturnType<typeof _useData>;
};

const TeamsPricing = ({ children, useData = _useData }: TeamsPricingProps) => {
  const { currency, isAnnual, isLoading, monthlyAmount } = useData();

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <Fragment>
      {children({ currency, price: monthlyAmount, isAnnual })}
    </Fragment>
  );
};

TeamsPricing.defaultProps = {
  children: ({ currency, isAnnual, price }: TeamsPricingChildProps) => (
    <Fragment>
      <Price currency={currency}>{price}</Price>
      {`/mo ${isAnnual ? '(paid annually)' : ''}`.trim()}
    </Fragment>
  ),
};

export default TeamsPricing;
